import { useState, useEffect, useRef } from "react";
import Chart from "chart.js/auto";
import { Interaction } from "chart.js";
import {
  PolarArea,
  getElementAtEvent,
  getDatasetAtEvent,
} from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
const Color = require("color");
Chart.register(ChartDataLabels);

function createColorGradient(startColor, endColor, steps) {
  // Convert the start and end colors to rgb format
  const startRGB = hexToRGB(startColor);
  const endRGB = hexToRGB(endColor);

  // Create an array to store the gradient colors
  const gradientColors = [];

  // Calculate the steps for each color channel
  const redStep = (endRGB.r - startRGB.r) / (steps - 1);
  const greenStep = (endRGB.g - startRGB.g) / (steps - 1);
  const blueStep = (endRGB.b - startRGB.b) / (steps - 1);

  // Generate the gradient colors
  for (let i = 0; i < steps; i++) {
    const red = Math.round(startRGB.r + redStep * i);
    const green = Math.round(startRGB.g + greenStep * i);
    const blue = Math.round(startRGB.b + blueStep * i);

    const hexColor = rgbToHex(red, green, blue);
    gradientColors.push(hexColor);
  }

  return gradientColors;
}

function createColorGradientWithMedian(
  startColor,
  medianColor,
  endColor,
  steps,
) {
  const beforeMedianSteps = Math.ceil(steps / 2);
  const afterMedianSteps = steps - beforeMedianSteps + 1;

  const beforeMedian = createColorGradient(
    startColor,
    medianColor,
    beforeMedianSteps,
  );
  const afterMedian = createColorGradient(
    medianColor,
    endColor,
    afterMedianSteps,
  );

  return [...beforeMedian, ...afterMedian.slice(1)];
}

// Helper functions to convert between hex and rgb color formats
function hexToRGB(hex) {
  const r = parseInt(hex.substring(1, 3), 16);
  const g = parseInt(hex.substring(3, 5), 16);
  const b = parseInt(hex.substring(5, 7), 16);
  return { r, g, b };
}

function rgbToHex(r, g, b) {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

Interaction.modes.myCustomMode = function (
  chart,
  e,
  _options,
  useFinalPosition,
) {
  const items = Interaction.modes.point(
    chart,
    e,
    { axis: "r", intersect: true },
    useFinalPosition,
  );
  const max = items.reduce((max, item) => {
    return max && max.datasetIndex < item.datasetIndex ? max : item;
  }, null);
  return max ? [max] : [];
};

const WheelOfBalance = ({
  areas,
  onAreaChange,
  maxRating,
  startColor,
  medianColor,
  endColor,
}) => {
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: [],
      },
    ],
  });

  const [orderedAreas, setOrderedAreas] = useState();

  const datasetsRating = {};
  // {
  //   0: 1,
  //   1: 1,
  //   2: 2,
  //   3: 2,
  //   4: 3,
  //   5: 3,
  //   6: 4,
  //   7: 4,
  // }
  for (let i = 0; i < maxRating; i++) {
    datasetsRating[i * 2] = i + 1;
    datasetsRating[i * 2 + 1] = i + 1;
  }

  const chartRef = useRef();
  const onClick = (event) => {
    const datasetsAtEvent = getDatasetAtEvent(chartRef.current, event);
    const element = getElementAtEvent(chartRef.current, event)[0];

    if (!element) {
      return;
    }

    const rating = datasetsRating[datasetsAtEvent[0].datasetIndex];
    const area = orderedAreas[element.element.$context.dataIndex];
    if (area.rating !== rating) {
      onAreaChange(area.name, { rating: rating });
    }
  };

  useEffect(() => {
    areas.sort((a, b) => a.rating - b.rating);

    let ordered = Array.from(Array(areas.length));
    // 0, 1,  2, 3,  4, 5,  6
    // (value / 2).ceil * (value % 2 === 0 ? 1 : -1)
    // 0, 1, -1, 2, -2, 3, -3
    areas.forEach(function (value, i) {
      let new_index = undefined;
      if (i === 0) {
        new_index = 0;
      } else if (i % 2 === 0) {
        new_index = Math.ceil(i / 2) * -1 + areas.length;
      } else {
        new_index = Math.ceil(i / 2);
      }
      ordered[new_index] = value;
    });

    setOrderedAreas(ordered);

    const labels = ordered.map((area) => area.name);
    const colors = createColorGradientWithMedian(
      startColor,
      medianColor,
      endColor,
      maxRating,
    );
    const datasets = [];

    for (let rating = 0; rating < maxRating; rating++) {
      const ratingColor = colors[rating];
      const lightColor = Color(ratingColor).lightness(95).hex();

      const ratingsData = ordered.map((area) =>
        area.rating > rating ? rating + 1 : 0,
      );
      const ratingsToGoData = ordered.map((area) =>
        area.rating <= rating ? rating + 1 : 0,
      );

      datasets.push(
        {
          data: ratingsData,
          backgroundColor: ratingColor,
        },
        {
          data: ratingsToGoData,
          backgroundColor: lightColor,
        },
      );
    }

    setChartData({
      labels,
      datasets: datasets,
    });
  }, [areas]);

  return (
    <div className="flex h-full w-full flex-col items-center justify-center">
      <PolarArea
        data={chartData}
        ref={chartRef}
        onClick={onClick}
        options={{
          responsive: true,
          scales: {
            r: {
              min: 0,
              max: maxRating + 1,
              ticks: {
                display: false,
                stepSize: 1,
                callback: (val) => {
                  return val <= maxRating ? "" : null;
                },
                z: 1,
              },
              startAngle: (360 / areas.length / 2) * -1,
            },
          },
          interaction: { mode: "myCustomMode" },
          aspectRatio: 1.5,
          layout: { padding: 20 },
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
            datalabels: {
              formatter: function (value, context) {
                const area = orderedAreas[context.dataIndex];
                return value === maxRating ? area.name.split(" ") : "";
              },
              align: "end",
              anchor: "end",
              color: "#000",
              font: {
                size: 14,
                weight: "bold",
              },
              textAlign: "center",
            },
          },
        }}
      />
    </div>
  );
};

export default WheelOfBalance;
